import {
  useGetLayoutListNameIdQuery,
  type PostFilter,
} from "@package/ui-gql/index-data-access";
import { type WritableDraft } from "immer";
import { uniq } from "lodash";
import { useMemo, useState } from "react";
import { useItemGridFilterContext } from "../ItemGridFilter/context-item-grid-filter";
import {
  ItemGridFilterDataList,
  type FilterValueEntry,
} from "../ItemGridFilter/ItemGridFilterDataList";

type SupportedFilter = PostFilter | null | undefined;

export function ItemGridFilterLayouts() {
  const [query, setQuery] = useState<string>("");

  const { filter, updateFilterState } = useItemGridFilterContext();
  const { data, loading, error } = useGetLayoutListNameIdQuery({
    variables: {
      page: 0,
      perPage: 1000,
      sortField: "name",
      sortOrder: "ASC",
      filter: (query && { q: query }) || undefined,
    },
  });

  const filterWithLayouts: SupportedFilter = filter;
  const items = useMemo<FilterValueEntry[]>(() => {
    const values: FilterValueEntry[] =
      data?.items
        ?.slice()
        ?.filter((item) => !!item?.id)
        ?.map((item) => {
          const id = item.id ?? "";
          const name = item.name ?? "";
          const checked = filterWithLayouts?.layouts?.includes(id) ?? false;
          return {
            id,
            name,
            checked,
          };
        }) ?? [];

    return values;
  }, [data?.items, filterWithLayouts?.layouts]);

  return (
    <ItemGridFilterDataList
      error={error}
      items={items}
      loading={loading}
      property="layouts"
      text="Layouts"
      onQueryValueChanged={setQuery}
      onRequestFilterReset={() =>
        updateFilterState((draft: WritableDraft<SupportedFilter>) => {
          if (draft) {
            delete draft.layouts;
          }
        })
      }
      onRequestFilterUpdate={(id, include) =>
        updateFilterState((draft: WritableDraft<SupportedFilter>) => {
          if (draft) {
            if (include) {
              draft.layouts = uniq([...(draft.layouts ?? []), id]);
            } else if (draft.layouts?.length) {
              draft.layouts = draft.layouts.filter((layout) => layout !== id);
            }

            if (!draft.layouts?.length) {
              delete draft.layouts;
            }
          }
        })
      }
      onSelectAllChanged={(checked) => {
        if (checked) {
          updateFilterState((draft: WritableDraft<SupportedFilter>) => {
            if (draft) {
              draft.layouts = data?.items
                ?.slice()
                ?.filter((item) => !!item?.id)
                ?.map((item) => item.id ?? "");
            }
          });
        } else {
          updateFilterState((draft: WritableDraft<SupportedFilter>) => {
            if (draft) {
              delete draft.layouts;
            }
          });
        }
      }}
    />
  );
}
